import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  Instagram,
  Share2,
  ShoppingCart,
  Sparkles,
  Star,
  Tag,
} from "lucide-react";
import { fetchStoreWithStock } from "@/lib/storeFetch";
import { currencySymbol } from "@/lib/currency";
import { useRobotsMeta, markPrerenderReady } from "@/lib/seo";
import { useStorefrontCart } from "@/lib/storefrontCart";
import StorefrontCartButton from "@/components/StorefrontCartButton";
import RichText from "@/components/RichText";

const NEW_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
function isRecentlyAdded(product) {
  if (!product.created_at) return false;
  return Date.now() - new Date(product.created_at).getTime() < NEW_WINDOW_MS;
}

// Only https links are allowed for the merchant's Buy / affiliate link (same rule as the Instagram DM card).
function safeHttpsUrl(u) {
  return typeof u === "string" && /^https:\/\/\S+$/i.test(u.trim()) ? u.trim() : null;
}

/**
 * PublicProductPage.jsx
 * ------------------------------------------------------------------
 * Individual, shareable product page. Route: /store/:username/p/:slug
 *
 * Reuses the SAME cached payload as PublicStorefront.jsx (GET
 * /public/store?u=:username) — no new endpoint, no new cache, no new
 * DB read per visit. The product is just picked out of the already-
 * fetched list by slug, client-side.
 *
 * Sets real per-product title/description/OG/Twitter tags and a Product
 * JSON-LD block so link previews (WhatsApp, Instagram, iMessage, Slack)
 * and search engines see rich, accurate content. Our nginx config
 * proxies known bots to a headless-Chrome "prerender" service that
 * snapshots this page AFTER markPrerenderReady() fires, so bots always
 * see the final tags, never a half-loaded state.
 *
 * Only Pro-plan merchants' product pages are indexable — Starter and
 * Growth still get a fully shareable page with rich previews, just
 * marked noindex, matching the "Not searchable on Google" note shown
 * on the storefront listing page for those plans.
 * ------------------------------------------------------------------
 */
export default function PublicProductPage() {
  const { username, slug } = useParams();
  const [store, setStore] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | product_not_found | not_found | error
  const [activeImage, setActiveImage] = useState(0);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState("");
  const cart = useStorefrontCart(username);
  const [selectedSize, setSelectedSize] = useState(null);
  const [stockMap, setStockMap] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetchStoreWithStock(username)
      .then(({ store: storeData, stockMap: stockData }) => {
        if (cancelled) return;
        setStore(storeData);
        setStockMap(stockData);
        const found = (storeData.products || []).some((p) => p.slug === slug);
        setStatus(found ? "ok" : "product_not_found");
      })
      .catch((e) => {
        if (cancelled) return;
        setStatus(e?.response?.status === 404 ? "not_found" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [username, slug]);

  const product = useMemo(
    () => store?.products?.find((p) => p.slug === slug) || null,
    [store, slug]
  );

  // Buy / affiliate link (set in Catalog). "link_only" = no Add to cart, customer goes straight to the merchant's link.
  const buyUrl = safeHttpsUrl(product?.external_url);
  const buyLabel = ((product?.external_label || "").trim()) || "🛒 Shop now";
  const isLinkOnly = !!buyUrl && product?.dm_action === "link_only";

  const gallery = useMemo(
    () => (product ? [product.image_url, ...(product.additional_image_urls || [])].filter(Boolean) : []),
    [product]
  );

  const sizes = useMemo(
    () => (product?.sizes && !isLinkOnly ? product.sizes.split(",").map((s) => s.trim()).filter(Boolean) : []),
    [product, isLinkOnly]
  );

  useEffect(() => {
    if (sizes.length > 0) setSelectedSize((cur) => cur || sizes[0]);
  }, [sizes]);

  // Starts (and stays, while loading/on error/on non-Pro) noindex — only
  // flips once we know for certain this merchant is on the Pro plan.
  useRobotsMeta(store?.plan === "pro" ? "index, follow" : "noindex, nofollow");

  useEffect(() => {
    if (status !== "loading") markPrerenderReady();
  }, [status]);

  // Stock and the catalog version are loaded together with the store payload
  // (see fetchStoreWithStock in lib/storeFetch.js).

  const stock = product ? stockMap[product.id] : null;
  const stockBySize = stock?.stock_by_size || {};
  const isSizeOutOfStock = (s) => {
    const q = stockBySize[s];
    return q !== undefined && q !== null && Number(q) <= 0;
  };
  const availableSizes = sizes.length > 0 ? sizes.filter((s) => !isSizeOutOfStock(s)) : sizes;
  const overallOutOfStock =
    sizes.length === 0 && stock && stock.overall_qty !== undefined && stock.overall_qty !== null && Number(stock.overall_qty) <= 0;
  const allSizesOutOfStock = sizes.length > 0 && stock && availableSizes.length === 0;
  const isOutOfStock = !isLinkOnly && (overallOutOfStock || allSizesOutOfStock);

  useEffect(() => {
    if (selectedSize && isSizeOutOfStock(selectedSize) && availableSizes.length > 0) {
      setSelectedSize(availableSizes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock]);

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 3200);
  };

  const canonicalUrl = typeof window !== "undefined" ? window.location.href : "";
  const symbol = store ? currencySymbol(store.currency) : "";

  const copyDetails = async () => {
    if (!product) return;
    try {
      await navigator.clipboard.writeText(product.name);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      flash("Copied! Paste it when you message us.");
    } catch {
      flash("Couldn't copy — try again.");
    }
  };

  const addToCart = () => {
    if (!product) return;
    const raw = selectedSize ? stockBySize[selectedSize] : stock?.overall_qty;
    const available = raw === undefined || raw === null ? null : Number(raw);
    if (available !== null) {
      const existing = cart.items.find(
        (l) => l.catalog_item_id === product.id && (l.size || null) === (selectedSize || null)
      );
      const currentQty = existing ? existing.quantity : 0;
      if (currentQty >= available) {
        flash(available <= 0 ? "This item is out of stock." : `Only ${available} left — that's already in your cart.`);
        return;
      }
    }
    cart.addItem({
      catalog_item_id: product.id,
      name: product.name,
      price: Number(product.price),
      image_url: product.image_url,
      size: selectedSize || null,
      quantity: 1,
      is_digital: !!product.is_digital,
    });
    flash("Added to cart!");
  };

  const openInstagram = () => {
    window.open(`https://www.instagram.com/${username}/`, "_blank", "noopener,noreferrer");
  };

  const shareLink = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: product?.name, url });
      } catch {
        // user cancelled the share sheet
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        flash("Link copied!");
      } catch {
        flash("Couldn't copy the link.");
      }
    }
  };

  return (
    <section className="min-h-screen bg-cream px-6 py-12 md:px-14 md:py-16">
      {status === "ok" && store && product && (
        <Helmet>
          <title>{`${product.name} — @${store.ig_username}`}</title>
          <meta
            name="description"
            content={(product.description || `${product.name} — ${symbol}${Number(product.price)} from @${store.ig_username} on CheckoutDM.`).slice(0, 160)}
          />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:type" content="product" />
          <meta property="og:title" content={product.name} />
          <meta
            property="og:description"
            content={(product.description || `Message @${store.ig_username} on Instagram to order.`).slice(0, 200)}
          />
          {gallery[0] && <meta property="og:image" content={gallery[0]} />}
          <meta property="og:url" content={canonicalUrl} />
          <meta property="product:price:amount" content={String(product.price)} />
          <meta property="product:price:currency" content={store.currency} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={product.name} />
          {gallery[0] && <meta name="twitter:image" content={gallery[0]} />}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "Product",
              name: product.name,
              image: gallery,
              description: product.description || undefined,
              sku: String(product.id),
              brand: { "@type": "Brand", name: store.ig_username },
              offers: {
                "@type": "Offer",
                url: canonicalUrl,
                priceCurrency: store.currency,
                price: Number(product.price),
                availability: "https://schema.org/InStock",
              },
            })}
          </script>
          {product.faqs?.length > 0 && (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org/",
                "@type": "FAQPage",
                mainEntity: product.faqs.map((f) => ({
                  "@type": "Question",
                  name: f.question,
                  acceptedAnswer: { "@type": "Answer", text: f.answer },
                })),
              })}
            </script>
          )}
        </Helmet>
      )}

      <div className="mx-auto max-w-4xl">
        {notice && (
          <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-cream shadow-lg">
            {notice}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <Link
            to={`/store/${username}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-hotpink"
          >
            <ArrowLeft size={14} /> Back to @{username}'s catalog
          </Link>
          <StorefrontCartButton username={username} currency={store?.currency} cart={cart} stockMap={stockMap} />
        </div>

        {status === "loading" && (
          <div className="mt-10 grid h-64 place-items-center text-sm text-ink/50">Loading…</div>
        )}

        {(status === "not_found" || status === "product_not_found") && (
          <div className="mt-10 grid h-64 place-items-center text-center">
            <div>
              <p className="text-2xl font-black text-ink">
                {status === "not_found" ? "Store not found" : "Product not found"}
              </p>
              <p className="mt-2 text-sm text-ink/50">
                {status === "not_found"
                  ? "This link may be wrong, or the merchant hasn't turned on their public catalog."
                  : "This product may have been removed or renamed."}
              </p>
              <a
                href="/"
                className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream hover:bg-ink/85"
              >
                Visit CheckoutDM.com <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="mt-10 grid h-64 place-items-center text-center">
            <p className="text-sm text-ink/50">Something went wrong loading this product. Try refreshing.</p>
          </div>
        )}

        {status === "ok" && store && product && (
          <div className="mt-6 grid gap-8 md:grid-cols-2">
            <div>
              <div className="relative aspect-square w-full overflow-hidden rounded-3xl border border-ink/10 bg-ink/5">
                {gallery.length > 0 ? (
                  <img src={gallery[activeImage]} alt={product.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full w-full place-items-center text-xs text-ink/30">No image</div>
                )}
                {isRecentlyAdded(product) && (
                  <span className="absolute left-3 top-3 rounded-full bg-mint px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
                    <Sparkles size={10} className="mr-1 inline" /> New
                  </span>
                )}
                {product.is_bestseller && (
                  <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
                    <Star size={10} className="fill-ink" /> Bestseller
                  </span>
                )}
                {gallery.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setActiveImage((i) => (i - 1 + gallery.length) % gallery.length)}
                      aria-label="Previous photo"
                      className="absolute left-2 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-full bg-ink/50 text-white backdrop-blur-sm hover:bg-ink/70"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveImage((i) => (i + 1) % gallery.length)}
                      aria-label="Next photo"
                      className="absolute right-2 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-full bg-ink/50 text-white backdrop-blur-sm hover:bg-ink/70"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </>
                )}
              </div>
              {gallery.length > 1 && (
                <div className="mt-3 flex gap-2">
                  {gallery.map((url, i) => (
                    <button
                      key={url + i}
                      type="button"
                      onClick={() => setActiveImage(i)}
                      className={`h-16 w-16 flex-none overflow-hidden rounded-xl border-2 ${
                        i === activeImage ? "border-hotpink" : "border-transparent"
                      }`}
                    >
                      <img src={url} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              {product.category && (
                <span className="inline-flex items-center gap-1 rounded-full bg-hotpink/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-hotpink">
                  <Tag size={10} /> {product.category}
                </span>
              )}
              <h1 className="mt-3 text-3xl font-black tracking-tight text-ink">{product.name}</h1>
              <p className="mt-2 text-2xl font-bold text-ink">
                {Number(product.price) === 0 ? "Free" : `${symbol}${Number(product.price)}`}
              </p>
              {product.description && (
                <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-ink/70">{product.description}</p>
              )}

              {product.extended_description && (
                <RichText text={product.extended_description} className="mt-4 text-sm leading-relaxed text-ink/70" />
              )}

              {availableSizes.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-1.5">
                  {availableSizes.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSelectedSize(s)}
                      className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition ${
                        selectedSize === s ? "bg-ink text-cream" : "border border-ink/15 bg-white text-ink/60 hover:bg-ink/5"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                {isLinkOnly ? (
                  <a
                    href={buyUrl}
                    target="_blank"
                    rel="sponsored noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-95"
                  >
                    {buyLabel} <ArrowUpRight size={14} />
                  </a>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={addToCart}
                      disabled={isOutOfStock}
                      className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ShoppingCart size={14} /> {isOutOfStock ? "Sold out" : "Add to cart"}
                    </button>
                    {buyUrl && (
                      <a
                        href={buyUrl}
                        target="_blank"
                        rel="sponsored noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:bg-ink/5"
                      >
                        {buyLabel} <ArrowUpRight size={14} />
                      </a>
                    )}
                  </>
                )}
                {isOutOfStock && (
                  <span className="inline-flex items-center rounded-full bg-ink/5 px-3 py-1 text-xs font-semibold text-ink/50">
                    Currently out of stock
                  </span>
                )}
                <button
                  type="button"
                  onClick={copyDetails}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:bg-ink/5"
                >
                  {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />} Copy details
                </button>
                <button
                  type="button"
                  onClick={openInstagram}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:bg-ink/5"
                >
                  <Instagram size={14} /> Message @{username}
                </button>
                <button
                  type="button"
                  onClick={shareLink}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:bg-ink/5"
                >
                  <Share2 size={14} /> Share
                </button>
              </div>

              {(product.sizing_guide || product.care_instructions || product.delivery_info) && (
                <div className="mt-8 space-y-4 border-t border-ink/10 pt-6">
                  {product.sizing_guide && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Sizing guide</p>
                      <RichText text={product.sizing_guide} className="mt-1 text-sm text-ink/70" />
                    </div>
                  )}
                  {product.care_instructions && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Care instructions</p>
                      <RichText text={product.care_instructions} className="mt-1 text-sm text-ink/70" />
                    </div>
                  )}
                  {product.delivery_info && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Delivery</p>
                      <RichText text={product.delivery_info} className="mt-1 text-sm text-ink/70" />
                    </div>
                  )}
                </div>
              )}

              {product.faqs?.length > 0 && (
                <div className="mt-6 space-y-3 border-t border-ink/10 pt-6">
                  <p className="text-xs font-bold uppercase tracking-wide text-ink/50">FAQs</p>
                  {product.faqs.map((f, i) => (
                    <div key={i}>
                      <p className="text-sm font-semibold text-ink">{f.question}</p>
                      <p className="mt-0.5 text-sm text-ink/70">{f.answer}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}